import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Package, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getOrder } from "@/lib/api";
import { buildTelegramLink } from "@/config";

const gbp = (n) => `£${n.toFixed(2)}`;

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getOrder(id).then(setOrder).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div data-testid="order-not-found" className="mx-auto max-w-xl px-4 py-28 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Order not found</h1>
        <p className="mt-3 text-slate-600">We couldn't find that order. If you've paid, message us on Telegram and we'll sort it.</p>
        <Link to="/shop"><Button className="mt-6 btn-metallic font-semibold">Back to shop</Button></Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24">
        <Skeleton className="h-64 w-full rounded-2xl bg-slate-100" />
      </div>
    );
  }

  return (
    <div className="pt-16 pb-24">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="text-center">
          {order.payment_status === "Paid" ? (
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </span>
          ) : (
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-amber-500/10 ring-1 ring-amber-500/30">
              <AlertCircle className="h-8 w-8 text-amber-500" />
            </span>
          )}
          <h1 data-testid="order-heading" className="mt-6 font-display text-3xl font-bold tracking-tight text-slate-900">{order.payment_status === "Paid" ? "Order confirmed" : "Payment not completed"}</h1>
          <p className="mt-3 text-slate-600">{order.payment_status === "Paid" ? `Thank you, ${order.full_name}. Your payment was received and our UK team will be in touch to confirm dispatch.` : "We haven't received payment for this order, so it hasn't gone through. You can return to checkout and try again."}</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <span data-testid="order-number" className="inline-block rounded-full glass px-4 py-1.5 font-mono2 text-sm text-slate-800">Order {order.order_number}</span>
            {order.payment_status === "Paid" && (
              <span data-testid="paid-badge" className="inline-block rounded-full bg-emerald-100 px-3 py-1.5 font-mono2 text-xs font-semibold text-emerald-700">Paid</span>
            )}
          </div>
        </motion.div>

        <div className="mt-10 rounded-2xl glass-strong metallic-border p-6">
          <div className="flex items-center gap-2 text-slate-800"><Package className="h-5 w-5" /> <span className="font-display font-semibold">Summary</span></div>
          <div className="mt-5 space-y-3">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-slate-700">{i.name} <span className="text-slate-500">× {i.qty}</span></span>
                <span className="text-slate-800">{gbp(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2 border-t border-slate-200 pt-5 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="text-slate-800">{gbp(order.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Shipping</span><span className="text-slate-800">{order.shipping === 0 ? "Free" : gbp(order.shipping)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-3 text-base font-semibold"><span className="text-slate-900">Total</span><span className="metallic-text">{gbp(order.total)}</span></div>
          </div>
          <div className="mt-5 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
            <p className="text-slate-700">Delivering to</p>
            <p className="mt-1">{order.address}, {order.city}, {order.postcode}, {order.country}</p>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a href={buildTelegramLink(`Hello PepsWorld UK, this is regarding my order ${order.order_number}.`)} target="_blank" rel="noreferrer" className="flex-1">
            <Button variant="outline" className="w-full h-11 border-emerald-600/40 bg-transparent text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"><MessageCircle className="mr-2 h-4 w-4" /> Message us about this order</Button>
          </a>
          <Link to={order.payment_status === "Paid" ? "/shop" : "/checkout"} className="flex-1"><Button data-testid="order-primary-action" className="w-full h-11 btn-metallic font-semibold">{order.payment_status === "Paid" ? "Continue shopping" : "Return to checkout"}</Button></Link>
        </div>
      </div>
    </div>
  );
}
